import {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useRef,
  useContext,
} from "react";
import { Modal, Row, Col, Spinner, Button, FormControl } from "react-bootstrap";
import { Download, Trash, FileEarmarkArrowUp } from "react-bootstrap-icons";
import { useParams } from "react-router-dom";
import { WithApiAccess } from "../lib/HigherOrderComponents/WithApiAccess";
import { LoadingSpinner } from "../lib/components/LoadingSpinner";
import { FormattedDate } from "../lib/components/FormattedDate";
import { ContractLayout } from "./ContractLayout";
import { withHeaderMessages } from "../higherOrderComponents/WithHeaderMessages";
import { Review } from "./DocumentUpload/Review";
import { VALID_EXTENSIONS, FILE_SIZE_LIMIT } from "./DocumentUpload/Utils";
import { AuthContext } from "../lib/contexts/AuthContext";

const ContractDocuments = (props) => {
  const { hasClaim } = useContext(AuthContext);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [activeAction, setActiveAction] = useState(null);

  const initialState = {
    contract: {
      provider: {
        address: {},
      },
      purchaser: {
        address: {},
      },
      recipients: [],
    },
    contractDocuments: [],
    detailsLoading: true,
    documentsLoading: true,
  };

  const reducer = (state, action) => {
    switch (action.type) {
      case "contractDetails":
        return {
          ...state,
          contract: action.payload,
          detailsLoading: false,
        };
      case "contractDocuments":
        return {
          ...state,
          contractDocuments: action.payload,
          documentsLoading: false,
        };
      case "hideDocumentsLoader":
        return {
          ...state,
          documentsLoading: false,
        };
      case "error":
        return {
          ...state,
          error: action.payload,
        };
      default:
        throw new Error();
    }
  };

  const [contractDocumentsState, dispatch] = useReducer(reducer, initialState);
  const { number } = useParams();
  useEffect(() => {
    async function getContractDetails() {
      props.header.setHeader("contractDocuments");
      const res = await props.api.getContract(`/api/contract/${number}`);
      dispatch({ type: "contractDetails", payload: res.data });
    }
    getContractDetails();
  }, []);

  const getDocuments = useCallback(async () => {
    try {
      const res = await props.api.get(`/api/contract/${number}/documents`);
      dispatch({ type: "contractDocuments", payload: res.data });
    } catch (e) {
      hideDocumentsLoader();
    }
  }, []);

  useEffect(() => {
    getDocuments();
  }, [getDocuments]);

  const hideDocumentsLoader = () => {
    dispatch({ type: "hideDocumentsLoader" });
  };

  const documentUploadRequestIsValid = (file) => {
    const ext = file.name.split(".").pop().toLowerCase();
    if (!VALID_EXTENSIONS.includes(ext)) {
      props.api.addError(
        `Invalid file type. Accepted types: ${VALID_EXTENSIONS.join(", ")}`,
      );
      fileInputRef.current.value = null;
      return false;
    }

    if (file.size > FILE_SIZE_LIMIT) {
      props.api.addError(
        `File exceeds the ${FILE_SIZE_LIMIT / 1024 / 1024}MB size limit.`,
      );
      fileInputRef.current.value = null;
      return false;
    }

    return true;
  };

  const submitUpload = async () => {
    setIsSubmitting(true);
    if (documentUploadRequestIsValid(selectedFile)) {
      const formData = new FormData();
      const metadata = [];

      formData.append("documents", selectedFile, selectedFile.name);

      metadata.push({
        fileName: selectedFile.name,
        contractFileType: selectedDocument.documentType,
        contractNumber: contract.number,
        providerCode: contract.provider.code,
        displayName: selectedFile.name,
      });

      formData.append("metadata", JSON.stringify(metadata));
      formData.append("className", selectedDocument.className);
      formData.append("documentUploadTypeName", selectedDocument.documentType);

      try {
        var response = await props.api.putForm(
          `/api/contract/${number}/documents/${selectedDocument.fileGuid}`,
          formData,
        );
        if (response.status === 200) {
          props.api.addSuccess(
            "Your document has been replaced. It may take up to 15 minutes for this page to refresh.",
          );
          getDocuments();
        }
      } catch (ex) {}
    }
    setActiveAction(null);
    setIsSubmitting(false);
    setShowModal(false);
  };

  const handleReplace = (document) => {
    setIsSubmitting(false);
    fileInputRef.current.value = null;
    setSelectedDocument(document);
    fileInputRef.current.click();
  };

  const handleFileSelected = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!documentUploadRequestIsValid(file)) {
      return;
    }

    setActiveAction({ guid: selectedDocument.fileGuid, type: "replacing" });
    setSelectedFile(file);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedFile(null);
    setSelectedDocument(null);
    setActiveAction(null);
  };

  const download = async (url, guid, event) => {
    setActiveAction({ guid: guid, type: "download" });
    dispatch({ type: "error", payload: "" });
    await props.api
      .getBlob(`/${url}`)
      .then((response) => {
        if (response.status === 200) {
          var filename = "";
          var disposition = response.headers["content-disposition"];
          if (disposition && disposition.indexOf("attachment") !== -1) {
            var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            var matches = filenameRegex.exec(disposition);
            if (matches != null && matches[1]) {
              filename = matches[1].replace(/['"]/g, "");
            }
          }

          return {
            filename: filename,
            blob: new Blob([response.data], { type: response.data.type }),
          };
        }
      })
      .then((file) => {
        let blobUrl = window.URL.createObjectURL(file.blob);

        let anchor = document.createElement("a");
        anchor.href = blobUrl;
        anchor.download = file.filename;
        document.body.appendChild(anchor);
        anchor.click();
        setTimeout(function () {
          document.body.removeChild(anchor);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);
        setActiveAction(null);
      })
      .catch((error) => {
        setActiveAction(null);
        dispatch({
          type: "error",
          payload:
            "Error retrieving document. Please try again later or contact support if the problem persists.",
        });
      });
  };

  const downloadFile = async (contractNumber, fileGuid, event) => {
    await download(
      `api/contract/${contractNumber}/documents/${fileGuid}`,
      fileGuid,
      event,
    );
  };

  const deleteFile = async (contractNumber, fileGuid, event) => {
    setActiveAction({ guid: fileGuid, type: "delete" });

    await props.api
      .delete(`api/contract/${contractNumber}/documents/${fileGuid}`)
      .then((response) => {
        if (response.status === 200) {
          props.api.addSuccess(
            "Your document has been deleted. It may take up to 15 minutes for this page to refresh.",
          );
          getDocuments();
        }
      })
      .finally(() => {
        setActiveAction(null);
      });
  };

  if (
    contractDocumentsState.detailsLoading ||
    contractDocumentsState.documentsLoading
  ) {
    return <LoadingSpinner />;
  }

  const { contract, contractDocuments } = contractDocumentsState;
  const disableButtons = activeAction !== null;

  const assignments =
    selectedFile && contract
      ? [
          {
            id: selectedFile.name,
            name: selectedFile.name,
            size: selectedFile.size,
            contract,
          },
        ]
      : [];

  return (
    <ContractLayout
      contractNumber={number}
      contractStatus={contract.status}
      className="border-top border-4"
    >
      <FormControl
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelected}
        accept={VALID_EXTENSIONS.map((e) => `.${e}`)}
        style={{ display: "none" }}
      />
      <Modal show={showModal} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Replace Document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Review assignments={assignments} />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleClose}
            style={{ minWidth: "150px" }}
          >
            Cancel
          </Button>
          <Button
            variant="dark"
            size="sm"
            disabled={isSubmitting}
            onClick={submitUpload}
            style={{ minWidth: "150px" }}
          >
            Replace
          </Button>
        </Modal.Footer>
      </Modal>
      <Row className="section">
        <Col md={12}>
          <table className="table">
            <thead>
              <tr>
                <th>Class</th>
                <th>Document Type</th>
                <th>Name</th>
                <th>Created</th>
                <th></th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(contractDocuments || []).map((d) => (
                <tr key={d.fileGuid}>
                  <td>{d.className}</td>
                  <td>{d.documentType}</td>
                  <td>{d.fileName}</td>
                  <td>
                    <FormattedDate date={d.created} />
                  </td>
                  <td style={{ position: "relative" }}>
                    <button
                      onClick={(event) =>
                        downloadFile(number, d.fileGuid, event)
                      }
                      disabled={disableButtons}
                      aria-label="Download"
                      className="btn p-0 border-0 "
                      title="Download"
                    >
                      {activeAction?.guid === d.fileGuid &&
                      activeAction?.type === "download" ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <Download />
                      )}
                    </button>
                  </td>
                  <td>
                    {hasClaim("DocumentUploadsContractFiles") && d.canBeDeleted && (
                      <button
                        onClick={() => handleReplace(d)}
                        disabled={disableButtons}
                        aria-label="Replace document"
                        className="btn p-0 border-0"
                        title="Replace"
                      >
                        {activeAction?.guid === d.fileGuid &&
                        activeAction?.type === "replace" ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <FileEarmarkArrowUp />
                        )}
                      </button>
                    )}
                  </td>
                  <td>
                    {hasClaim("DocumentUploadsContractFiles") && d.canBeDeleted && (
                      <button
                        onClick={(event) =>
                          deleteFile(number, d.fileGuid, event)
                        }
                        disabled={disableButtons}
                        aria-label="Delete"
                        className="btn p-0 border-0"
                        title="Delete"
                      >
                        {activeAction?.guid === d.fileGuid &&
                        activeAction?.type === "delete" ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <Trash />
                        )}
                      </button>
                    )}
                  </td>
                </tr>
              ))}

              {(!contractDocuments || contractDocuments.length === 0) && (
                <tr key="nonefound">
                  <td colSpan={7}>No documents found</td>
                </tr>
              )}
            </tbody>
          </table>
        </Col>
      </Row>
    </ContractLayout>
  );
};

export default withHeaderMessages(WithApiAccess(ContractDocuments));
